import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CURRENCIES, CURRENCY_BY_CODE, COMMON_CURRENCY_CODES } from '../../lib/currencies.js';

// Searchable currency picker for expense line items. Replaces the old
// fixed 10-item dropdown: every ISO currency is available, you type to
// filter (by code OR name — "yen", "JPY", "pound" all work), and the
// currencies you actually use float to the top under "Recent" so you
// rarely have to search at all.
//
// Recents live in localStorage (per browser), seeded with the currencies
// ProFun uses most so the list is useful on day one and then adapts to
// each person. The stored value is just the ISO code (e.g. "USD"), so the
// line-item data shape is unchanged.
//
// The dropdown renders through a portal with fixed positioning so it can't
// be clipped by the expense modal's overflow-y-auto body (an inline
// absolute dropdown only showed ~2 rows there), and it flips above the
// field when there isn't room below.

const RECENTS_KEY = 'prohr.expense.currency.recents';
const MAX_RECENTS = 8;
const DROPDOWN_MAX_PX = 288; // keep in sync with max-h-72 below

function loadRecents() {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENTS_KEY) || 'null');
    if (Array.isArray(raw) && raw.length) {
      const valid = raw.filter((c) => CURRENCY_BY_CODE[c]);
      if (valid.length) return valid.slice(0, MAX_RECENTS);
    }
  } catch { /* corrupt/disabled storage — fall back to the seed */ }
  return COMMON_CURRENCY_CODES.slice(0, MAX_RECENTS);
}

function recordRecent(code) {
  try {
    const cur = loadRecents();
    const next = [code, ...cur.filter((c) => c !== code)].slice(0, MAX_RECENTS);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch { /* storage disabled — recents just won't persist */ }
}

const INPUT_CLASS =
  'flex h-10 w-full rounded-lg border border-border bg-card px-3.5 py-2 text-sm text-foreground shadow-sm ring-offset-background transition-all duration-150 placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:border-primary/50';

export default function CurrencyCombobox({ value, onChange, label = 'Currency' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [recents, setRecents] = useState(loadRecents);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const dropRef = useRef(null);

  // Position the portaled dropdown against the input, flipping above when
  // the field sits too close to the bottom of the viewport.
  const reposition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const flipUp = below < DROPDOWN_MAX_PX && r.top > below;
    setPos({
      left: r.left,
      width: r.width,
      top: flipUp ? undefined : r.bottom + 4,
      bottom: flipUp ? window.innerHeight - r.top + 4 : undefined,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    reposition();
    const onScroll = () => reposition();
    // Capture phase so we also catch scrolling inside the modal body.
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (dropRef.current?.contains(e.target)) return;
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', onDoc);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [open, reposition]);

  const q = query.trim().toLowerCase();

  // While searching, show a single flat list of matches (code or name).
  // Idle, show "Recent" first, then every other currency under "All".
  let groups;
  if (q) {
    const matches = CURRENCIES.filter(
      (c) => c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q),
    ).slice(0, 60);
    groups = [{ header: null, items: matches }];
  } else {
    const recentItems = recents.map((code) => CURRENCY_BY_CODE[code]).filter(Boolean);
    const recentSet = new Set(recents);
    const rest = CURRENCIES.filter((c) => !recentSet.has(c.code));
    groups = [
      ...(recentItems.length ? [{ header: 'Recent', items: recentItems }] : []),
      { header: 'All currencies', items: rest },
    ];
  }
  const flat = groups.flatMap((g) => g.items);

  const pick = (code) => {
    onChange(code);
    recordRecent(code);
    setRecents(loadRecents());
    setOpen(false);
    setQuery('');
    setActiveIndex(-1);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      setQuery('');
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const sel = flat[activeIndex] || flat[0];
      if (sel) pick(sel.code);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  // Running index so the keyboard highlight maps across grouped rows.
  let rowIndex = -1;

  return (
    <div ref={wrapRef} className="relative flex flex-col gap-1.5">
      {label && <label className="text-[13px] font-medium text-muted-foreground">{label}</label>}
      <input
        ref={inputRef}
        value={open ? query : value || ''}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIndex(-1); }}
        onFocus={(e) => { setOpen(true); setQuery(''); e.target.select(); }}
        onKeyDown={onKeyDown}
        placeholder="Search currency…"
        className={INPUT_CLASS}
        autoComplete="off"
        spellCheck={false}
      />
      {open && flat.length > 0 && pos && createPortal(
        <div
          ref={dropRef}
          style={{ position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, zIndex: 60 }}
          className="max-h-72 overflow-y-auto rounded-lg border border-border bg-card shadow-lg"
        >
          {groups.map((g) => (
            <div key={g.header || 'matches'}>
              {g.header && (
                <div className="px-3.5 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {g.header}
                </div>
              )}
              {g.items.map((c) => {
                rowIndex += 1;
                const i = rowIndex;
                return (
                  <button
                    key={c.code}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); pick(c.code); }}
                    onMouseEnter={() => setActiveIndex(i)}
                    className={`flex w-full items-center gap-2 px-3.5 py-2 text-left text-sm transition-colors ${
                      i === activeIndex ? 'bg-primary/10 text-foreground' : 'text-foreground hover:bg-accent'
                    } ${c.code === value ? 'font-semibold' : ''}`}
                  >
                    <span className="w-10 shrink-0 font-mono text-xs">{c.code}</span>
                    <span className="truncate text-muted-foreground">{c.name}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
